import React, { useState, useMemo } from 'react';
import { 
  Gift, Cake, Send, CalendarHeart, 
  CheckSquare, Square, Users, Sparkles, MessageCircle
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA NERVIOSO CENTRAL (Ruta Absoluta)
import { useGlobal } from 'context/GlobalContext';

const MuroCelebraciones = () => {
  const { ninos, maestrosGlobal } = useGlobal();
  
  const [pestaña, setPestaña] = useState('ninos'); 
  const [seleccionados, setSeleccionados] = useState([]);

  // --- LÓGICA DE DETECCIÓN DE CUMPLEAÑEROS REALES ---
  const fechaHoy = new Date();
  const mesActual = fechaHoy.getMonth(); // 0-11

  const cumpleanerosNinos = useMemo(() => {
    return ninos.filter(n => {
      if (!n.fechaNacimiento) return false;
      const mesCumple = new Date(n.fechaNacimiento).getMonth();
      return mesCumple === mesActual;
    }).map(n => ({
      ...n,
      dia: new Date(n.fechaNacimiento).getDate(),
      pasado: new Date(n.fechaNacimiento).getDate() < fechaHoy.getDate()
    })).sort((a, b) => a.dia - b.dia);
  }, [ninos, mesActual, fechaHoy]);

  const cumpleanerosMaestros = useMemo(() => {
    return maestrosGlobal.filter(m => {
      if (!m.nacimiento) return false;
      const mesCumple = new Date(m.nacimiento).getMonth();
      return mesCumple === mesActual;
    }).map(m => ({
      ...m,
      dia: new Date(m.nacimiento).getDate()
    })).sort((a, b) => a.dia - b.dia);
  }, [maestrosGlobal, mesActual]);

  const toggleSeleccion = (id) => {
    setSeleccionados(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // --- MOTOR DE MENSAJES EMPÁTICOS ---
  const enviarMensajeWhatsApp = (nombre) => {
    const primerNombre = nombre.split(' ')[0];
    const msg = `¡Hola Familia! 🎉 En IgleKids estamos de fiesta por el cumpleaños de ${primerNombre}. Queremos decirles que es un regalo tenerlo con nosotros y oramos por un nuevo año lleno de la gracia de Dios. ¡Felicidades! ❤️`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  return (
    <div className="w-full max-w-2xl mx-auto space-y-8 animate-in fade-in duration-500 pb-32 px-4 text-left font-sans">
      
      {/* CABECERA FESTIVA */}
      <div className="text-center space-y-2 relative pt-8">
        <div className="absolute top-0 left-0 w-full flex justify-between px-4 opacity-10 pointer-events-none">
          <Cake size={60} className="text-amber-500 rotate-12" />
          <Gift size={60} className="text-fuchsia-500 -rotate-12" />
        </div>
        
        <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-600 px-6 py-2 rounded-full mb-2 border border-amber-100 shadow-sm relative z-10">
          <Sparkles size={18} className="animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] italic">Cultura de Honra</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-black text-slate-900 italic tracking-tighter uppercase leading-none">Celebraciones</h2>
        <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest italic">{meses[mesActual]} 2026</p>
      </div>

      {/* SELECTOR DE CATEGORÍA */}
      <div className="flex bg-white p-2 rounded-[2.5rem] shadow-xl border-2 border-slate-50 gap-2">
        <button 
          onClick={() => {setPestaña('ninos'); setSeleccionados([]);}}
          className={`flex-1 py-4 rounded-[1.8rem] flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-widest transition-all ${
            pestaña === 'ninos' ? 'bg-[#0A2440] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'
          }`}
        >
          <Users size={18} /> Exploradores
        </button>
        <button 
          onClick={() => {setPestaña('maestros'); setSeleccionados([]);}}
          className={`flex-1 py-4 rounded-[1.8rem] flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-widest transition-all ${
            pestaña === 'maestros' ? 'bg-[#0A2440] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'
          }`}
        >
          <CalendarHeart size={18} /> Staff / Maestros
        </button>
      </div>

      {/* LISTADO DINÁMICO */}
      <div className="space-y-4">
        {pestaña === 'ninos' ? (
          <>
            {cumpleanerosNinos.length === 0 ? (
              <div className="py-24 text-center opacity-30 font-black uppercase italic tracking-widest">Sin cumpleaños registrados este mes</div>
            ) : (
              cumpleanerosNinos.map((nino) => (
                <div 
                  key={nino.id}
                  onClick={() => toggleSeleccion(nino.id)}
                  className={`p-6 rounded-[3rem] border-4 transition-all cursor-pointer flex items-center gap-6 ${
                    seleccionados.includes(nino.id) 
                      ? 'bg-amber-50 border-amber-400 shadow-xl scale-[1.02]' 
                      : 'bg-white border-slate-50 shadow-sm hover:border-amber-100'
                  }`}
                >
                  <div className={`${seleccionados.includes(nino.id) ? 'text-amber-500' : 'text-slate-200'}`}>
                    {seleccionados.includes(nino.id) ? <CheckSquare size={32} /> : <Square size={32} />}
                  </div>

                  <div className="flex-1 text-left">
                    <div className="flex justify-between items-start">
                      <h4 className="text-2xl font-black text-slate-800 italic uppercase tracking-tighter leading-none">{nino.nombre}</h4>
                      <span className="text-[9px] font-black uppercase text-amber-600 bg-white border border-amber-200 px-3 py-1 rounded-full shadow-sm">
                        {nino.edad || '--'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-3">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                        <CalendarHeart size={14} className={nino.pasado ? 'text-slate-300' : 'text-rose-500'} /> 
                        {nino.dia} de {meses[mesActual]} {nino.pasado && '(Celebrado)'}
                      </p>
                      <p className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-lg uppercase italic tracking-tighter">{nino.salon}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        ) : (
          <div className="grid gap-4">
            {cumpleanerosMaestros.length === 0 ? (
              <div className="py-24 text-center opacity-30 font-black uppercase italic tracking-widest">No hay festejos de staff este mes</div>
            ) : (
              cumpleanerosMaestros.map((maestro) => (
                <div key={maestro.id} className="bg-white p-6 rounded-[3rem] border-4 border-slate-50 shadow-sm flex items-center justify-between group hover:border-fuchsia-200 transition-all">
                  <div className="flex items-center gap-5 text-left">
                    <div className="w-16 h-16 rounded-[1.8rem] bg-fuchsia-50 text-fuchsia-500 flex items-center justify-center shadow-inner">
                      <Cake size={32} />
                    </div>
                    <div>
                      <h4 className="text-2xl font-black text-slate-800 italic uppercase tracking-tighter leading-none">{maestro.nombre}</h4>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2 flex items-center gap-2">
                        {maestro.dia} de {meses[mesActual]} • <span className="text-indigo-500 italic">{maestro.rol || 'Staff'}</span>
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); enviarMensajeWhatsApp(maestro.nombre); }}
                    className="w-14 h-14 bg-fuchsia-100 text-fuchsia-600 rounded-[1.5rem] flex items-center justify-center hover:bg-fuchsia-600 hover:text-white transition-all shadow-lg active:scale-90"
                  >
                    <Send size={24} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* PANEL DE ACCIÓN DINÁMICO */}
      {seleccionados.length > 0 && (
        <div className="fixed bottom-24 left-0 w-full px-6 z-50 animate-in slide-in-from-bottom-10 duration-300">
          <div className="max-w-md mx-auto bg-[#0A2440] p-8 rounded-[3.5rem] shadow-2xl space-y-6 border-t-4 border-amber-500">
            {seleccionados.length === 1 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white text-left">
                  <Cake className="text-amber-400" size={24} />
                  <h4 className="font-black italic text-xl uppercase tracking-tighter leading-none">Mención Especial</h4>
                </div>
                <button 
                  onClick={() => enviarMensajeWhatsApp(cumpleanerosNinos.find(n => n.id === seleccionados[0]).nombre)}
                  className="w-full py-6 bg-emerald-500 text-white rounded-[2.5rem] font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 active:scale-95 transition-all shadow-xl border-b-4 border-emerald-700"
                >
                  <MessageCircle size={20} /> Enviar Abrazo Digital
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white text-left">
                  <div className="p-3 bg-amber-500 rounded-2xl text-slate-900 shadow-lg">
                    <Cake size={24} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-black italic text-xl uppercase tracking-tighter leading-none">Celebración Masiva</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-1 italic">Notificar a los grupos del salón</p>
                  </div>
                </div>
                <button 
                  onClick={() => {alert("Campaña de Festejo Colectivo enviada a los canales de padres."); setSeleccionados([]);}}
                  className="w-full py-6 bg-amber-500 text-slate-900 rounded-[2.5rem] font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 active:scale-95 transition-all shadow-xl border-b-4 border-amber-600"
                >
                  <Send size={20} /> Lanzar Felicitación Grupal
                </button>
              </div>
            )}
            <button onClick={() => setSeleccionados([])} className="w-full text-[10px] font-black text-slate-500 uppercase tracking-widest italic hover:text-slate-400 transition-colors">Deseleccionar todos</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuroCelebraciones;